// src/pages/Pets.js

import React, { useReducer, useEffect } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  Paper,
  Typography,
  Toolbar as MuiToolbar,
} from '@mui/material';
import PetRow from './PetRow'; // Import PetRow
import Pagination from '../components/Pagination'; // Import PetPagination
import PetsTableHead from '../components/PetsTableHead'; // Import PetsTableHead
import { petsReducer, initialState } from '../reducers/petsReducer'; // Import reducer
import { ACTIONS } from "../reducers/listActions";
import { handleSortRequest } from "../utils/sortingUtils";
import api from "../services/api";

const columns = [
  { id: 'redirect', label: '', sortable: false }, // For Redirect Icon
  { id: 'name', label: 'Name', sortable: true },
  { id: 'breed', label: 'Breed', sortable: false },
  { id: 'sex', label: 'Sex', sortable: false },
  { id: 'weight', label: 'Weight', sortable: false },
  { id: 'color', label: 'Color', sortable: false },
  { id: 'age', label: 'Age', sortable: false },
  { id: 'medicalIssues', label: 'Medical Issues', sortable: false },
  { id: 'customer', label: 'Customer', sortable: false },
  { id: 'comments', label: '', sortable: false },
  { id: 'imageUrl', label: '', sortable: false },
];

function Pets({ data }) {
  const [state, dispatch] = useReducer(petsReducer, initialState);
  const {
    pets,
    loading,
    error,
    order,
    orderBy,
    page,
    rowsPerPage,
    totalPets,
  } = state;

  const isSearchMode = !!data;
  const displayedPets = isSearchMode ? data : pets;

  useEffect(() => {
    if (isSearchMode) return;

    const fetchPets = async () => {
      dispatch({ type: ACTIONS.FETCH_INIT });
      try {
        const token = localStorage.getItem('token'); // Retrieve the token
        if (!token) {
          throw new Error('No token found');
        }
        const response = await api.get('/pets', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: page + 1, // Backend expects 1-based page index
            limit: rowsPerPage,
            sortBy: orderBy,
            order: order,
          },
        });
        dispatch({
          type: ACTIONS.FETCH_SUCCESS,
          payload: {
            pets: response.data.data,
            total: response.data.total,
          },
        });
      } catch (err) {
        console.error('Error fetching pets:', err);
        dispatch({ type: ACTIONS.FETCH_FAILURE, payload: 'Failed to fetch pets.' });
      }
    };

    fetchPets();
  }, [page, rowsPerPage, order, orderBy, isSearchMode]);

  const handleSort = handleSortRequest(orderBy, order, dispatch);

  const handleChangePage = (event, newPage) => {
    dispatch({ type: ACTIONS.SET_PAGE, payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: ACTIONS.SET_ROWS_PER_PAGE,
      payload: parseInt(event.target.value, 10),
    });
  };

  return (
    <>
      <MuiToolbar />
      <Paper sx={{ padding: '1rem', width: '97%', margin: '1rem auto' }}>
        <Typography variant="h4" gutterBottom>
          Pets
        </Typography>
        {loading ? (
          <Typography>Loading pets...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : displayedPets.length === 0 ? (
          <Typography>No pets found.</Typography>
        ) : (
          <>
            {/* Top Pagination */}
            {!isSearchMode && (
              <Pagination
                count={totalPets} // e.g., totalCustomers or totalServices
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}

            <Table>
              <TableHead>
                <PetsTableHead
                  columns={columns}
                  order={order}
                  orderBy={orderBy}
                  onSortRequest={handleSort} />
              </TableHead>
              <TableBody>
                {displayedPets.map((pet) => (
                  <PetRow key={pet.id} pet={pet} columns={columns} pets={displayedPets} />
                ))}
              </TableBody>
            </Table>

            {/* Bottom Pagination */}
            {!isSearchMode && (
              <Pagination
                count={totalPets} // e.g., totalCustomers or totalServices
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </>
        )}
      </Paper>
    </>
  );
}

export default Pets;